/**
 * Public read-only waiver text page.
 *
 * Sachin 2026-03-05: the "Read the full waiver" button on
 * /welcome/waiver used to link to /waiver, but that page is a
 * pre-sign flow that requires a `?request_id=` from a booking email
 * — without one it shows "MISSING BOOKING". Parents just wanted to
 * READ the waiver, not sign again.
 *
 * This page has no gate: any visitor can hit /waiver-read and see
 * the current member-side WAIVER_TEXT with a friendly header, a
 * printable layout, and links back to the signing flow / home.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { WAIVER_TEXT, WAIVER_VERSION } from "@/lib/waiver";

export default function WaiverRead() {
    return (
        <div className="min-h-screen bg-brand-obsidian text-white pt-28 pb-16 px-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(31,45,85,0.45),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(230,0,0,0.10),transparent_55%)] pointer-events-none" />
            <div className="relative max-w-3xl mx-auto">
                <Link
                    to="/dashboard"
                    data-testid="waiver-read-back"
                    className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white mb-6"
                >
                    <ArrowLeft className="w-3 h-3" />
                    Back
                </Link>

                <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                    <FileText className="w-3 h-3" />
                    Participant Agreement
                </div>
                <h1 className="font-display text-4xl md:text-5xl tracking-wider text-white leading-[0.95]">
                    THE FULL WAIVER.
                </h1>
                <p className="text-zinc-300 mt-3 leading-relaxed max-w-xl">
                    Read the whole thing here. When you&apos;re ready to sign, close this tab
                    and hit the checkbox on the sign page.
                </p>

                <div
                    data-testid="waiver-read-body"
                    className="mt-8 bg-black border border-zinc-900 rounded-lg p-6 sm:p-8 text-[13px] text-zinc-100 leading-relaxed whitespace-pre-wrap font-mono print:bg-white print:text-black print:border-0"
                >
                    {WAIVER_TEXT}
                </div>

                <p className="text-[11px] text-zinc-400 mt-4">
                    Waiver version <span className="text-zinc-200">{WAIVER_VERSION}</span>. Same form every athletic facility uses.
                </p>

                <div className="mt-8 flex flex-wrap gap-2">
                    <Link
                        to="/welcome/waiver"
                        data-testid="waiver-read-sign-cta"
                        className="btn-tactical inline-flex"
                    >
                        Ready to sign — continue
                    </Link>
                    <button
                        type="button"
                        onClick={() => window.print()}
                        data-testid="waiver-read-print"
                        className="btn-ghost-tactical inline-flex"
                    >
                        Print
                    </button>
                </div>
            </div>
        </div>
    );
}
